import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

// TODO tem que revisar as interfaces desses métodos.

/**
 * Representa a resposta de um participante/recursos humano em um projeto
 * 
 * @typedef {Object} Participante
 * @property {number} id - Identificador único do participante
 * @property {number|null} funcionario_id - ID do funcionário associado (pode ser nulo)
 * @property {Object|null} funcionario - Dados do funcionário associado (pode ser nulo)
 * @property {string} nome - Nome completo do participante/posição
 * @property {string|null} email - Email do participante (pode ser nulo)
 * @property {string|null} matricula - Número de matrícula (pode ser nulo)
 * @property {string|null} formacao - Formação acadêmica do participante (pode ser nulo)
 * @property {string|null} linkedin - URL do LinkedIn do participante (pode ser nulo)
 * @property {string|null} papel_projeto - Papel/função do participante no projeto (pode ser nulo)
 * @property {number} tipo_participante - Código numérico do tipo de participante
 * @property {string} tipo_participante_label - Descrição textual do tipo de participante
 * @property {number} perfil_id - ID do perfil profissional associado
 * @property {Perfil} perfil - Dados básicos do perfil profissional associado
 * @property {PerfilVigencia[]} perfis - Lista de perfis com períodos de vigência
 * @property {string|null} budget_key - Chave do orçamento associado (pode ser nulo)
 * @property {string[]} budget_keys - Lista de chaves de orçamento
 * @property {number|null} centro_custo_id - ID do centro de custo (pode ser nulo)
 * @property {CentroCusto|null} centro_custo - Dados do centro de custo (pode ser nulo)
 * @property {string} modificado_em - Data e hora da última modificação no formato ISO 8601
 */

/**
 * Representa os dados básicos de um perfil profissional
 * 
 * @typedef {Object} Perfil
 * @property {number} id - Identificador único do perfil
 * @property {string} especialidade - Área de atuação profissional
 * @property {string} senioridade - Nível de senioridade do profissional
 */

/**
 * Representa um perfil com período de vigência específico
 * 
 * @typedef {Object} PerfilVigencia
 * @property {number} id - Identificador único do perfil
 * @property {string} especialidade - Área de atuação profissional
 * @property {string} senioridade - Nível de senioridade do profissional
 * @property {string} competencia_inicio - Data de início de vigência no formato "YYYY-MM-DD"
 * @property {string|null} competencia_fim - Data de fim de vigência no formato "YYYY-MM-DD" (pode ser nulo)
 */

/**
 * Representa os dados de um centro de custo
 * 
 * @typedef {Object} CentroCusto
 * @property {number} id - Identificador único do centro de custo
 * @property {string} codigo - Código identificador do centro de custo
 * @property {string} nome - Nome do centro de custo
 * @property {number} business_unit_id - ID da unidade de negócio associada
 * @property {BusinessUnit} business_unit - Dados da unidade de negócio
 * @property {boolean} ativo - Indica se o centro de custo está ativo
 */

/**
 * Representa os dados de uma unidade de negócio
 * 
 * @typedef {Object} BusinessUnit
 * @property {number} id - Identificador único da unidade de negócio
 * @property {string} nome - Nome da unidade de negócio
 * @property {number} divisao_id - ID da divisão associada
 * @property {Divisao} divisao - Dados da divisão
 */

/**
 * Representa os dados de uma divisão
 * 
 * @typedef {Object} Divisao
 * @property {number} id - Identificador único da divisão
 * @property {string} nome - Nome da divisão
 */

const resource = `v1/ativacaoped/:empresaId/participantes`;
const LOADING_KEY = 'ativacaoped-participante';

/**
 * Lista participantes com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<Participante>>} Lista paginada de participantes
 */
export const getParticipantes = (empresaId, params = { page: 1, per_page: 10 }) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista participantes simplificados para componentes de seleção (dropdown, autocomplete).
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<Participante[]>} Lista de participantes
 */
export const getSelecaoParticipantes = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId).concat(`/selecao`);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Obtém um participante específico pelo ID.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} participanteId - ID do perfil
 * @returns {Promise<Perfil>} Dados do participante
 */
export const getParticipante = (empresaId, participanteId) => {
  const baseUrl = resource.replace(":empresaId", empresaId).concat(`/${participanteId}`);

  return withLoading(LOADING_KEY, () =>
    api.get(baseUrl).then(({ data }) => data)
  );
};

/**
 * Cria um novo participante.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} data - Dados do participante a ser criado
 * @property {number} data.tipo_participante - Código numérico do tipo de participante (obrigatório)
 * @param {string} data.funcionario_id - ID do funcionário associado (obrigatório)
 * @param {string} data.nome - Nome completo do participante/posição (obrigatório)
 * @param {string} data.email - Email do participante (obrigatório)
 * @param {string} [data.matricula] - Número de matrícula (opcional)
 * @param {string} [data.formacao] - Formação acadêmica do participante (opcional)
 * @returns {Promise<Participante>} Participante criado
 */
export const postParticipante = (empresaId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data || data)
  );
}

/**
 * Atualiza um participante existente.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} participanteId - ID do participante a ser atualizado
 * @param {Partial<Participante>} data - Dados parciais do participante para atualização
 * @returns {Promise<Participante>} Participante atualizado
 */
export const putParticipante = (empresaId, participanteId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.post(`${baseUrl}/${participanteId}`, data).then(({ data }) => data.data)
  );
}

/**
 * Altera o calculo do custo do participante em um BP.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} participanteId - ID do participante
 * @param {Object} data - Dados parciais do participante para atualização
 * @param {string|number} data.business_plan_id - ID do Business Plan
 * @param {number} data.percentual - porcentagem do Custo Médio do participante a ser considerada. Mín: -100. Max: 100
 * @param {string} data.justificativa - justificativa da calibragem. Max: 500 caracteres
 * @returns {Promise<void>}
 */
export const postParticipanteCalibragem = (empresaId, participanteId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.post(`${baseUrl}/${participanteId}/calibragem`, data)
  );
}

/**
 * Exclui um participante.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} participanteId - ID do participante a ser excluído
 * @returns {Promise<void>}
 */
export const deleteParticipante = (empresaId, participanteId) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.delete(`${baseUrl}/${participanteId}`)
  );
}

// TODO falta a implementação do método de exportação, mas faltam detalhes no Hoppscotch

export default {
  getParticipante,
  getParticipantes,
  getSelecaoParticipantes,
  postParticipante,
  putParticipante,
  deleteParticipante
};
